import React, { useState, useRef, useEffect } from "react";
import { useFileUpload } from "./hooks/useFileUpload";

const CameraCapture = ({ onCapture, onClose, videoRef }) => {
    const [isCapturing, setIsCapturing] = useState(true); // Flag to track whether the video is being captured
    const [capturedImage, setCapturedImage] = useState(null); // To store the captured image for preview
    const [currentDeviceId, setCurrentDeviceId] = useState(null); // To store the current selected camera device ID
    const [videoDevices, setVideoDevices] = useState([]); // Store all available video devices

    const handleCapture = () => {
        if (videoRef.current) {
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
                const file = new File([blob], "captured-image.png", { type: "image/png" });
                setCapturedImage(URL.createObjectURL(file)); // Set the captured image URL for preview
                setIsCapturing(false); // Stop capturing, show the preview
            });
        }
    };

    const handleRetake = () => {
        setCapturedImage(null);
        setIsCapturing(true);
    };

    const handleConfirm = () => {
        if(capturedImage) {
            onCapture(capturedImage);
        }
    };

    const handleClose = () => {
        if (onClose) {
            // stop video and audio tracks
            const stream = videoRef.current.srcObject;
            const tracks = stream.getTracks();
            tracks.forEach((track) => track.stop());

            onClose(); // Close the camera
        }
    };

    const startCamera = async (deviceId) => {
        try {
            if (deviceId) {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { deviceId: deviceId },
                });
                videoRef.current.srcObject = stream;
            } else {
                alert("No video devices found");
            }
        } catch (err) {
            console.error("Error accessing camera: ", err);
            alert("Error accessing camera");
        }
    };

    const handleDeviceChange = (event) => {
        const selectedDeviceId = event.target.value;
        setCurrentDeviceId(selectedDeviceId);
        startCamera(selectedDeviceId); // Restart the camera with the selected device
    };

    useEffect(() => {
        const getDevices = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter((device) => device.kind === "videoinput");
                setVideoDevices(videoDevices); // Store all video input devices
                if (videoDevices.length > 0) {
                    setCurrentDeviceId(videoDevices[0].deviceId); // Set the first device as the default
                    startCamera(videoDevices[0].deviceId); // Start the camera with the first available device
                }
            } catch (err) {
                console.error("Error fetching devices: ", err);
            }
        };

        getDevices();

        return () => {
            if (videoRef.current && videoRef.current.srcObject) {
                const stream = videoRef.current.srcObject;
                const tracks = stream.getTracks();
                tracks.forEach((track) => track.stop());
            }
        };
    }, []);

    return (
        <div className="camera-container" id="cameraContainer">
            <div className="camera-header">
                <button onClick={handleClose} className="close-button" id="closeButton">
                    X
                </button>
            </div>

            {isCapturing ? (
                <>
                    <video ref={videoRef} autoPlay playsInline id="video" />
                    {/* Camera Device Selection */}
                    <div className="device-selection" id="deviceSelection">
                        <select onChange={handleDeviceChange} value={currentDeviceId} id="deviceSelect">
                            {videoDevices.map((device) => (
                                <option key={device.deviceId} value={device.deviceId}>
                                    {device.label || `Camera ${videoDevices.indexOf(device) + 1}`}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button className="capture-button" onClick={handleCapture} id="captureButton">
                        Capture
                    </button>
                </>
            ) : (
                <>
                    <div className="preview-continer-wrapper">
                        <div className="preview-container" id="previewContainer">
                            <img src={capturedImage} alt="Captured" />
                        </div>
                        <div className="capture-actions" id="captureActions">
                            <button className="retake-button" onClick={handleRetake} id="retakeButton">
                                Retake
                            </button>
                            <button className="confirm-button" onClick={handleConfirm} id="confirmButton">
                                Confirm
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CameraCapture;
